import React from "react";
import { Vector4 } from "three";
import {
  getMainLineMesh,
  getLineMesh,
  drawPoint,
  updatePoint,
} from "./coverageBlueprint";
import CoveragePoint from "./models/CoveragePoint";
import getCoverageTicks from "./getCoverageTicks";
import Filter from "../../../../models/Filter";
import CoverageLine from "./models/CoverageLine";
import { COVERAGE_CL } from "../../constants/options";
import ColorScale from "../../models/ColorScale";

const restrictedIcon = (
  <i
    data-tooltip="The CNV results for this sample are restricted to a specific Gene List. <br/>Displaying this sample in the browser might reveal results that go beyond the selected genes."
    data-tooltip-class="genome-browser-small-tooltip"
    data-tooltip-dangerous-html="true"
    className="tw-text-red-500 fa fa-exclamation-circle tw-ml-2"
  />
);

function getDefaultState(track) {
  const { originAnalId, sampleCsv, qcReport, sampleId } =
    track.getSharedState();

  return {
    mainLines: [],
    referenceLines: [],
    visRange: [0, 1],
    sampleFilters: [],
    highlightKey: null,
    scaleFilter: new Filter({
      label: "Logarithmic",
      category: "Y Axis",
      key: "logScale",
      isVisible: true,
      totalCount: null,
      value: true,
      type: "toggle",
    }),
    sampleIndex: {},
    sampleId,
    sampleIdMap: qcReport?.sampleIdMap || {},
    sampleNameMap: qcReport?.sampleNameMap || {},
    globalMax: 1,
    max: 1,
    sampleCsv,
    originAnalId,
    colorInd: {},
    colorMap: [],
  };
}

function coverageTrackController(track) {
  let state = getDefaultState(track);
  const mainLineMesh = getMainLineMesh();
  const referenceLineMesh = getLineMesh();

  function onReset() {
    state = getDefaultState(track);
  }

  function onRangeChange({ chrSegments }) {
    const seqs = chrSegments.map((x) => ({
      ...x,
      x0: x.chr.region.x0,
      x1: x.chr.region.x1,
      g0: x.chr.region.g0,
      g1: x.chr.region.g1,
    }));

    const x0 = chrSegments[0]?.g0 || 0;
    const x1 = chrSegments[chrSegments.length - 1]?.g1 || 1;
    state.visRange = [x0, x1];
    track.loadPosition(seqs, state);
  }

  function updateSampleIndex(sampleId) {
    if (state.sampleIndex[sampleId] !== undefined) {
      return state.sampleIndex[sampleId];
    }
    const index = Object.values(state.sampleIndex).length;
    state.sampleIndex[sampleId] = index;
    return index;
  }

  function updateGraphSampleDetails(details) {
    const graphSampleDetails = Object.entries(details || {})
      .map(([k, v]) => [k, v?.user_sample_id])
      .reduce((a, [k, v]) => ({ ...a, [v]: k }), {});

    const sharedState = track.getSharedState();
    track.setSharedState({ ...sharedState, graphSampleDetails });
  }

  function onDataReceived({ res, chr }) {
    const { coverage_keys: covKeys, graph_sample_details: details } = res || {};
    const { sampleNameMap, sampleId } = state;
    const samples = covKeys.filter((x) => x.indexOf("graph") > -1);
    const keyIndex = covKeys.reduce((a, i, ind) => ({ ...a, [i]: ind }), {});
    const newData = {};

    updateGraphSampleDetails(details);

    updateSampleIndex(sampleId);

    for (let y = 0; y < samples.length; y++) {
      const sample = samples[y];
      const sampleName = details[sample].user_sample_id || sample;
      const id = sampleNameMap[sampleName]?.id || sampleName;
      const ind = keyIndex[sample];
      const startInd = keyIndex.start;
      const endInd = keyIndex.end;
      const arr = new Array(res.coverage.length * 2);

      for (let x = 0; x < res.coverage.length; x++) {
        const c = res.coverage[x];
        const start = c[startInd];
        const end = c[endInd];
        const logVal = Math.log10(Math.max(0.1, c[ind]));
        const val = c[ind];

        arr[x * 2] = new CoveragePoint(start, end, chr, val, logVal);
        arr[x * 2 + 1] = new CoveragePoint(end, end, chr, val, logVal);

        if (val > state.globalMax) state.globalMax = val;
      }
      if (arr.length === 0) return;

      newData[sample] = new CoverageLine(arr, id, chr, updateSampleIndex(id));
    }

    const mainLines = [state.sampleCsv].map((s) => newData[s]);
    const referenceLines = samples
      .filter((x) => x !== state.sampleCsv)
      .map((s) => newData[s]);

    state.mainLines = state.mainLines.concat(mainLines);
    state.referenceLines = state.referenceLines.concat(referenceLines);

    const mainLen = state.mainLines.reduce((a, i) => a + i.length, 0);
    const refLineLen = state.referenceLines.reduce((a, i) => a + i.length, 0);

    mainLineMesh.setItemLen(mainLen);
    referenceLineMesh.setItemLen(refLineLen);

    drawPoint(mainLines, mainLineMesh);
    drawPoint(referenceLines, referenceLineMesh);

    calcFilters();
    applyFilters();
    updateColorscale();
    track.setFilters([...state.sampleFilters, state.scaleFilter]);
    track.setOverlayProps({ mainLines: state.mainLines });

    updateAxis();
    render();
  }

  function render() {
    track.requestRender();
  }

  function updateAxis() {
    const filterSet = new Set(getActiveAnalyses());
    const [x0, x1] = state.visRange;
    const isLog = isLogScale();

    state.max = 0;

    const lines = state.mainLines
      .concat(state.referenceLines)
      .filter((x) => filterSet.has(x.analysis) || filterSet.size === 0)
      .filter((x) => x.region.hasOverlap(x0, x1));

    for (let l = 0; l < lines.length; l++) {
      const { points } = lines[l];
      for (let p = 0; p < points.length; p++) {
        const { region, val } = points[p];
        const hasOverlap = region.hasOverlap(x0, x1);
        if (hasOverlap) state.max = Math.max(val, state.max);
      }
    }

    state.max = state.max === 0 ? state.globalMax : state.max;
    const max = isLog ? Math.log10(state.max) : state.max;
    const min = isLog ? -1 : 0;

    mainLineMesh.uniforms.setMinY(max).setMaxY(min);
    referenceLineMesh.uniforms.setMinY(max).setMaxY(min);

    const ticks = getCoverageTicks(min, max, isLog);

    track.setAxisProps({ ticks });
    render();
  }

  function getActiveAnalyses() {
    return state.sampleFilters.filter((x) => x.value).map((x) => x.key);
  }

  function isLogScale() {
    return state.scaleFilter.value;
  }

  function onFiltersChange(updated) {
    state.sampleFilters = updated.filter((x) => x.key !== "logScale");
    state.scaleFilter = updated.filter((x) => x.key === "logScale").pop();
    track.setFilters([...updated]);
    setTimeout(() => {
      applyFilters();
      updateAxis();
    }, 100);
  }

  function onPreferencesChanged() {
    updateColorscale();
  }

  function updateColorscale() {
    const conf = track.getPreferences();
    const { isDark } = conf;
    const scaleName = conf[COVERAGE_CL];
    const theme = isDark ? 2 : 1;
    const vecs = new ColorScale(scaleName, 50).setTheme(theme).vectors;
    const { sampleIndex, sampleIdMap, sampleId } = state;

    const currentSample = sampleIdMap[sampleId];
    const refSet = currentSample?.refSampleIdSet || new Set();

    const mainCol = vecs[vecs.length - 1];
    const refCol = vecs[0];
    const gray = new Vector4(0.5, 0.5, 0.6, 1);

    state.colorInd = Object.keys(sampleIndex)
      .map((s) => {
        if (s === state.sampleId) return { sample: s, color: mainCol };
        if (refSet.has(s)) return { sample: s, color: refCol };
        return { sample: s, color: gray };
      })
      .reduce((a, i) => ({ ...a, [i.sample]: i.color }), {});

    Object.keys(state.colorInd).forEach((x) => {
      const ind = sampleIndex[x];
      vecs[ind] = state.colorInd[x];
    });

    state.colorMap = vecs;
    mainLineMesh.uniforms.setColorMap(state.colorMap);
    referenceLineMesh.uniforms.setColorMap(state.colorMap);
    track.requestRender();
  }

  function onRangeStabilized() {
    updateAxis();
  }

  function applyFilters(highlightSet) {
    const selected = getActiveAnalyses();
    const isLog = isLogScale();
    const analysesSet = highlightSet || new Set(selected);

    updatePoint(state.mainLines, mainLineMesh, isLog, analysesSet);
    updatePoint(state.referenceLines, referenceLineMesh, isLog, analysesSet);
    render();
  }

  function calcFilters() {
    const { sampleFilters: filters, sampleIdMap } = state;
    const sample = sampleIdMap[state.sampleId];
    const refSampleSet = sample?.refSampleIdSet || new Set();

    const current = state.mainLines[0]?.analysis;
    const valDict = filters.reduce((a, i) => ({ ...a, [i.key]: i.value }), {});
    const analysesSet = new Set(state.referenceLines.map((x) => x.analysis));
    const analysisArr = Array.from(analysesSet.values());

    const curFilter = new Filter({
      category: "Current Sample",
      key: current,
      value: valDict[current] ?? 1,
      label: sampleIdMap[current]?.name || current,
      isVisible: true,
      totalCount: null,
      helpTextAnchor: sampleIdMap[current]?.isRestricted
        ? restrictedIcon
        : null,
    });

    const refFilters = analysisArr
      .filter((x) => refSampleSet.has(x))
      .map((x) => {
        return new Filter({
          category: "Reference Samples",
          key: x,
          label: sampleIdMap[x]?.name || x,
          value: valDict[x] ?? !sampleIdMap[x]?.isRestricted,
          isVisible: true,
          totalCount: null,
          helpTextAnchor: sampleIdMap[x]?.isRestricted ? restrictedIcon : null,
        });
      });

    const otherFilters = analysisArr
      .filter((x) => !refSampleSet.has(x))
      .map((x) => {
        return new Filter({
          category: "Other Samples",
          key: x,
          label: sampleIdMap[x]?.name || x,
          value: valDict[x] ?? 0,
          isVisible: true,
          totalCount: null,
          helpTextAnchor: sampleIdMap[x]?.isRestricted ? restrictedIcon : null,
        });
      });

    state.sampleFilters = [curFilter, ...refFilters, ...otherFilters];
  }

  function onFilterHighlight(key) {
    const activeSamples = new Set(getActiveAnalyses());
    const noFilters = activeSamples.size === 0;
    const isNotFiltered = activeSamples.has(key) || noFilters;
    const isKeyActive = key && isNotFiltered;
    if (isKeyActive) {
      const newMap = state?.colorMap || [];
      const cMap = newMap.slice().map((x) => new Vector4().copy(x));
      const colorMap = cMap.map((x) => x.setW(0.1));
      const keyInd = state.sampleIndex[key];
      const found = cMap[keyInd];
      if (found) {
        colorMap[keyInd] = found.setW(1);
      }
      mainLineMesh.uniforms.setColorMap(colorMap);
      referenceLineMesh.uniforms.setColorMap(colorMap);
    } else {
      mainLineMesh.uniforms.setColorMap(state.colorMap);
      referenceLineMesh.uniforms.setColorMap(state.colorMap);
    }
    track.requestRender();
  }

  function onSharedStateChange() {
    const { previewId } = track.getSharedState();
    onFilterHighlight(previewId);
  }

  return {
    onReset,
    onFiltersChange,
    onFilterHighlight,
    onPreferencesChanged,
    onRangeChange,
    onRangeStabilized,
    onDataReceived,
    onSharedStateChange,
    meshes: [mainLineMesh, referenceLineMesh],
  };
}

export default coverageTrackController;
